// PERFORMANCE: Lazy load heavy components for code splitting
import { lazy, Suspense, useEffect } from "react";
import ErrorBoundary from "./components/common/ErrorBoundary";
import {
  startTokenRefreshMonitoring,
  stopTokenRefreshMonitoring,
} from "./utils/tokenRefresh";

// Lazy load screens for better performance
const AdminAnalytics = lazy(() => import("./screens/AdminAnalytics"));
const AdminDonations = lazy(() => import("./screens/AdminDonations"));
const AllCommissions = lazy(() => import("./screens/AllCommissions.new"));
const BusinessPlan = lazy(() => import("./screens/BusinessPlan"));
const ChangePassword = lazy(() => import("./screens/ChangePassword"));
const CommissionAnalyticsMain = lazy(
  () => import("./screens/CommissionAnalytics")
);
const DispatchManagement = lazy(() => import("./screens/DispatchManagement"));
const DeletedOrders = lazy(() => import("./screens/DeletedOrders"));
const RefundRequests = lazy(() => import("./screens/RefundRequests"));
const Donations = lazy(() => import("./screens/Donations"));
const MyOrders = lazy(() => import("./screens/MyOrders"));
const NormalOrders = lazy(() => import("./screens/NormalOrders"));
const OrderManagement = lazy(() => import("./screens/OrderManagement"));
const PaymentHistory = lazy(() => import("./screens/PaymentHistory"));
const RegistrationOrders = lazy(() => import("./screens/RegistrationOrders"));
const SystemConfig = lazy(() => import("./screens/SystemConfig"));
const PublicContent = lazy(() => import("./screens/PublicContent"));
const TeamCommissions = lazy(() => import("./screens/TeamCommissions"));
const TeamCommissionsList = lazy(() => import("./screens/TeamCommissionsList"));
const UserCommissionDetails = lazy(
  () => import("./screens/UserCommissionDetails")
);

// Import eagerly loaded components (auth screens)
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useNavigate,
} from "react-router-dom";
import ToastContainer from "./components/common/ToastNotification";
import Layout from "./components/Layout";
import { getPublicRoutes, ROUTES } from "./constants/routes";
import { AppProvider } from "./contexts/AppContext";
import { SystemConfigProvider } from "./contexts/SystemConfigContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import useToastNotifications, {
  ToastProvider,
} from "./hooks/useToastNotifications";
import AdminRoute from "./routes/AdminRoute";
import ProtectedRoute from "./routes/ProtectedRoute";
const ActivateDeactivateUsers = lazy(
  () => import("./screens/ActivateDeactivateUsers")
);
const AdminEmergencyWithdrawals = lazy(
  () => import("./screens/AdminEmergencyWithdrawals")
);
const Categories = lazy(() => import("./screens/Categories"));
const Dashboard = lazy(() => import("./screens/Dashboard"));
const EmergencyWithdrawals = lazy(
  () => import("./screens/EmergencyWithdrawals")
);
const Logs = lazy(() => import("./screens/Logs"));
const ManagePayments = lazy(() => import("./screens/ManagePayments"));
const ManualApproval = lazy(() => import("./screens/ManualApproval"));
const Products = lazy(() => import("./screens/Products"));
const ShippingZones = lazy(() => import("./screens/ShippingZones"));

// New feature screens
const TaxConfigScreen = lazy(() => import("./screens/TaxConfig"));
const CommissionBonuses = lazy(() => import("./screens/CommissionBonuses"));
const NotificationsScreen = lazy(() => import("./screens/NotificationsScreen"));
const Support = lazy(() => import("./screens/Support"));

// Auth screens (eager load for fast initial load)
import ForgotPassword from "./screens/ForgotPassword";
import Login from "./screens/Login";
import ResetPassword from "./screens/ResetPassword";

const Profile = lazy(() => import("./screens/Profile/Profile"));
const PromoteUserToModerator = lazy(
  () => import("./screens/PromoteUserToModerator")
);
const CommissionAnalytics = lazy(
  () => import("./screens/referralsAdmin/CommissionAnalytics")
);
const ReferralConfig = lazy(
  () => import("./screens/referralsAdmin/ReferralConfig")
);
const UserReferralNetwork = lazy(
  () => import("./screens/referralsAdmin/UserReferralNetwork")
);
const Withdrawals = lazy(() => import("./screens/referralsAdmin/Withdrawals"));
const Family = lazy(() => import("./screens/Family"));
const Wallet = lazy(() => import("./screens/Wallet"));

// Auth screens (eager load)
import Registration from "./screens/Registration";
import VerifyEmail from "./screens/VerifyEmail";

// Public marketing pages (lazy)
const HomePage = lazy(() => import("./screens/public/HomePage"));
const AboutUsPage = lazy(() => import("./screens/public/AboutUsPage"));
const ContactUsPage = lazy(() => import("./screens/public/ContactUsPage"));
const PublicProductsPage = lazy(
  () => import("./screens/public/ProductsPage")
);
const PrivacyPolicyPage = lazy(
  () => import("./screens/public/PrivacyPolicyPage")
);
const TermsAndConditionsPage = lazy(
  () => import("./screens/public/TermsAndConditionsPage")
);

function AuthRedirectHandler() {
  const navigate = useNavigate();
  const { addNotification } = useToastNotifications();
  useEffect(() => {
    // SECURITY: Start automatic token refresh monitoring
    startTokenRefreshMonitoring();

    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail as
        | { message?: string }
        | undefined;
      if (detail?.message) {
        addNotification({
          type: "warning",
          title: "Session Expired",
          message: detail.message,
        });
      }
      stopTokenRefreshMonitoring();
      // Only redirect if we're on a protected route
      const publicRoutes = getPublicRoutes();
      const currentPath = window.location.pathname;
      const isPublicRoute = publicRoutes.includes(currentPath);

      if (!isPublicRoute) {
        navigate(ROUTES.HOME, { replace: true });
      }
    };
    window.addEventListener("unauthorized", handler);
    return () => window.removeEventListener("unauthorized", handler);
  }, [navigate, addNotification]);
  return null;
}

// Loading fallback component for lazy loaded routes
const LoadingFallback = () => {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600">
          {/* Loading text handled by Suspense */}Loading...
        </p>
      </div>
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <SystemConfigProvider>
          <AppProvider>
            <ToastProvider>
              <Router>
                <AuthRedirectHandler />
                {/* Global Toast Host */}
                <GlobalToasts />
                <Suspense fallback={<LoadingFallback />}>
                  <Routes>
                    <Route
                      path={ROUTES.ROOT}
                      element={<Navigate to={ROUTES.HOME} replace />}
                    />

                    {/* Public marketing pages */}
                    <Route path={ROUTES.HOME} element={<HomePage />} />
                    <Route path={ROUTES.ABOUT} element={<AboutUsPage />} />
                    <Route path={ROUTES.CONTACT} element={<ContactUsPage />} />
                    <Route
                      path={ROUTES.PRODUCTS.PUBLIC_SHOP}
                      element={<PublicProductsPage />}
                    />
                    <Route
                      path={ROUTES.PRIVACY_POLICY}
                      element={<PrivacyPolicyPage />}
                    />
                    <Route
                      path={ROUTES.TERMS_AND_CONDITIONS}
                      element={<TermsAndConditionsPage />}
                    />
                    <Route path={ROUTES.LOGIN} element={<Login />} />
                    <Route path={ROUTES.REGISTER} element={<Registration />} />
                    <Route
                      path={ROUTES.VERIFY_EMAIL}
                      element={<VerifyEmail />}
                    />
                    <Route
                      path={ROUTES.FORGOT_PASSWORD}
                      element={<ForgotPassword />}
                    />
                    <Route
                      path={ROUTES.RESET_PASSWORD}
                      element={<ResetPassword />}
                    />
                    <Route element={<ProtectedRoute />}>
                      <Route element={<Layout />}>
                        <Route
                          path={ROUTES.DASHBOARD}
                          element={<Dashboard />}
                        />
                        <Route path={ROUTES.WALLET} element={<Wallet />} />
                        <Route path={ROUTES.PROFILE} element={<Profile />} />
                        <Route
                          path={ROUTES.CHANGE_PASSWORD}
                          element={<ChangePassword />}
                        />

                        {/* Business Plan Page (all users) */}
                        <Route
                          path={ROUTES.BUSINESS_PLAN}
                          element={<BusinessPlan />}
                        />

                        {/* Family Network Screen (all users) */}
                        <Route
                          path={ROUTES.REFERRALS.FAMILY}
                          element={<Family />}
                        />

                        {/* Member Commission Screens (all users) */}
                        <Route
                          path={ROUTES.COMMISSIONS.MY}
                          element={<TeamCommissionsList />}
                        />
                        <Route
                          path={ROUTES.COMMISSIONS.ROOT}
                          element={<Navigate to={ROUTES.DASHBOARD} replace />}
                        />

                        {/* Orders (user screens) */}
                        <Route
                          path={ROUTES.ORDERS.ROOT}
                          element={<OrderManagement />}
                        />
                        <Route
                          path={ROUTES.ORDERS.PLACE}
                          element={<OrderManagement />}
                        />
                        <Route path={ROUTES.ORDERS.MY} element={<MyOrders />} />

                        {/* Donations (user) */}
                        <Route
                          path={ROUTES.DONATIONS.USER}
                          element={<Donations />}
                        />

                        {/* Emergency Withdrawals (user) */}
                        <Route
                          path={ROUTES.EMERGENCY.USER}
                          element={<EmergencyWithdrawals />}
                        />

                        {/* KYC (user) */}
                        <Route
                          path={ROUTES.KYC.USER}
                          element={<Navigate to={ROUTES.DASHBOARD} replace />}
                        />

                        {/* Returns (user) */}
                        <Route
                          path={ROUTES.RETURNS.USER}
                          element={<Navigate to={ROUTES.DASHBOARD} replace />}
                        />

                        {/* Invoices (user) */}
                        <Route
                          path={ROUTES.INVOICES.USER}
                          element={<Navigate to={ROUTES.DASHBOARD} replace />}
                        />

                        {/* Commission Bonuses (user) */}
                        <Route path={ROUTES.COMMISSION_BONUSES.USER} element={<CommissionBonuses />} />

                        {/* My Commission Withdrawals (user) */}
                        <Route
                          path={ROUTES.REFERRALS.MY_WITHDRAWALS}
                          element={<Navigate to={ROUTES.DASHBOARD} replace />}
                        />

                        {/* Notifications (all users) */}
                        <Route path={ROUTES.NOTIFICATIONS} element={<NotificationsScreen />} />

                        {/* Support Tickets (user) */}
                        <Route path={ROUTES.SUPPORT.USER} element={<Support />} />

                        {/* ===== ADMIN-ONLY ROUTES ===== */}
                        <Route element={<AdminRoute />}>
                          <Route
                            path={ROUTES.REFERRALS.MANUAL_APPROVAL}
                            element={<ManualApproval />}
                          />
                          <Route
                            path={ROUTES.MANAGE_PAYMENTS}
                            element={<ManagePayments />}
                          />
                          <Route
                            path={ROUTES.ACTIVATE_DEACTIVATE_USERS}
                            element={<ActivateDeactivateUsers />}
                          />
                          <Route
                            path={ROUTES.PROMOTE_USER_TO_MODERATOR}
                            element={<PromoteUserToModerator />}
                          />
                          <Route path={ROUTES.LOGS} element={<Logs />} />
                          <Route
                            path={ROUTES.PRODUCTS.MANAGE}
                            element={<Products />}
                          />
                          <Route
                            path={ROUTES.CATEGORIES.MANAGE}
                            element={<Categories />}
                          />
                          <Route
                            path={ROUTES.SHIPPING.ZONES}
                            element={<ShippingZones />}
                          />
                          <Route
                            path={ROUTES.DISPATCH_MANAGEMENT}
                            element={<DispatchManagement />}
                          />
                          <Route
                            path={ROUTES.SYSTEM_CONFIG}
                            element={<SystemConfig />}
                          />
                          <Route
                            path={ROUTES.PUBLIC_CONTENT}
                            element={<PublicContent />}
                          />
                          <Route
                            path={ROUTES.DONATIONS.ADMIN}
                            element={<AdminDonations />}
                          />
                          {/* Referrals Admin Screens */}
                          <Route
                            path={ROUTES.REFERRALS.ADMIN.CONFIG}
                            element={<ReferralConfig />}
                          />
                          <Route
                            path={ROUTES.REFERRALS.ADMIN.ANALYTICS}
                            element={<CommissionAnalytics />}
                          />
                          <Route
                            path={ROUTES.REFERRALS.ADMIN.WITHDRAWALS}
                            element={<Withdrawals />}
                          />
                          <Route
                            path={ROUTES.REFERRALS.ADMIN.USER_NETWORK}
                            element={<UserReferralNetwork />}
                          />
                          {/* Admin Commission Screens */}
                          <Route
                            path={ROUTES.COMMISSIONS.ALL}
                            element={<AllCommissions />}
                          />
                          <Route
                            path={ROUTES.COMMISSIONS.USER_DETAILS}
                            element={<UserCommissionDetails />}
                          />
                          <Route
                            path={ROUTES.COMMISSIONS.PAYMENT_HISTORY}
                            element={<PaymentHistory />}
                          />
                          <Route
                            path={ROUTES.COMMISSIONS.TEAM}
                            element={<TeamCommissions />}
                          />
                          <Route
                            path={ROUTES.COMMISSIONS.ANALYTICS}
                            element={<CommissionAnalyticsMain />}
                          />
                          <Route
                            path={ROUTES.EMERGENCY.ADMIN}
                            element={<AdminEmergencyWithdrawals />}
                          />
                          {/* Admin Order Screens */}
                          <Route
                            path={ROUTES.ORDERS.REGISTRATION}
                            element={<RegistrationOrders />}
                          />
                          <Route
                            path={ROUTES.ORDERS.NORMAL}
                            element={<NormalOrders />}
                          />
                          <Route
                            path={ROUTES.ORDERS.DELETED}
                            element={<DeletedOrders />}
                          />
                          <Route
                            path={ROUTES.ORDERS.REFUND_REQUESTS}
                            element={<RefundRequests />}
                          />
                          <Route
                            path={ROUTES.ADMIN_ANALYTICS}
                            element={<AdminAnalytics />}
                          />
                          {/* Admin KYC */}
                          <Route
                            path={ROUTES.KYC.ADMIN}
                            element={<Navigate to={ROUTES.DASHBOARD} replace />}
                          />
                          {/* Admin Returns */}
                          <Route
                            path={ROUTES.RETURNS.ADMIN}
                            element={<Navigate to={ROUTES.DASHBOARD} replace />}
                          />
                          {/* Tax Config (Admin) */}
                          <Route path={ROUTES.TAX_CONFIG} element={<TaxConfigScreen />} />
                          {/* Admin Commission Bonuses */}
                          <Route path={ROUTES.COMMISSION_BONUSES.ADMIN} element={<CommissionBonuses />} />
                          {/* Admin Support */}
                          <Route path={ROUTES.SUPPORT.ADMIN} element={<Support />} />
                        </Route>
                      </Route>
                    </Route>
                  </Routes>
                </Suspense>
              </Router>
            </ToastProvider>
          </AppProvider>
        </SystemConfigProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

// Renders a single toast host for the whole app
const GlobalToasts: React.FC = () => {
  const { notifications, removeNotification } = useToastNotifications();
  return (
    <ToastContainer
      notifications={notifications}
      onClose={removeNotification}
    />
  );
};

export default App;
